import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Gift, Printer, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function GiftStatus() {
  const { token } = useParams();
  const [g, setG] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let tries = 0;
    let timer;
    const load = () => api.get(`/gifts/public/${token}`).then((r) => {
      setG(r.data);
      if (r.data.status === "unpaid" && tries++ < 15) timer = setTimeout(load, 4000);
    }).catch(() => setError("We couldn't find that gift."));
    load();
    return () => clearTimeout(timer);
  }, [token]);

  const finishPayment = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/gifts/public/${token}/pay`, { origin_url: window.location.origin }, { timeout: 60000 });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open checkout — please call 310-422-0669"); setBusy(false); }
  };

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300" data-testid="gift-notfound">{error} <Link to="/gift" className="dg-link">Buy a gift session</Link></div>;
  if (!g) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400">Loading…</div>;
  const paid = g.status !== "unpaid";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="gift-status">
      <div className="dg-card rounded-2xl p-8 text-center">
        {paid ? <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" /> : <Clock className="w-12 h-12 text-amber-400 mx-auto" />}
        <div className="dg-eyebrow mt-4">{paid ? "Gift ready" : "Waiting for payment"}</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold font-display" data-testid="gift-status-title">
          {paid ? `${g.recipient_name}'s session is paid for.` : "Almost there."}
        </h1>
        <p className="text-slate-300 mt-3">{g.service_name} · ${Number(g.amount_usd).toFixed(2)} · from {g.buyer_name}</p>
        {paid ? (
          <>
            <div className="mt-6 inline-block font-mono text-2xl tracking-[0.2em] text-[#FFD600] bg-[#0B1120] border border-slate-700 rounded-xl px-6 py-3" data-testid="gift-code">{g.code}</div>
            <p className="text-sm text-slate-400 mt-3">Gift code · never expires · we've emailed it to you{g.status === "redeemed" ? " · already redeemed" : ""}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to={`/gift/card/${token}`} className="dg-btn-primary rounded-full px-6 py-3 text-sm flex items-center gap-2" data-testid="gift-print-btn"><Printer className="w-4 h-4" /> Print or save the gift card</Link>
              <Link to="/book" className="dg-btn-ghost rounded-full px-6 py-3 text-sm" data-testid="gift-book-link">Redeem it now</Link>
            </div>
          </>
        ) : (
          <>
            <p className="text-slate-400 mt-4 text-sm">We haven't received the payment yet. If you closed the checkout, you can finish paying below — this page updates automatically once it goes through.</p>
            <button type="button" onClick={finishPayment} disabled={busy} className="dg-btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 mt-5 disabled:opacity-50" data-testid="gift-pay-again">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} {busy ? "Opening checkout…" : "Finish payment"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
